/**
 * ClaimOfficialDataPage — Gestor flow to claim official results data.
 *
 * Two-phase post-onboarding: search a canonical club → submit claim → wait for review.
 * Displays existing claims with status badges and evidence submission for "needs_more_info".
 *
 * Route: /manager/claim
 * Guard: GESTOR / ADMIN_MASTER role
 * See: SPEC_OFFICIAL_DATA_CLAIM.md
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VUKZ, VUKZ_GRADIENT } from '../../theme/vukzTheme';
import {
  Search, Loader2, CheckCircle2, AlertCircle, ChevronRight,
  Shield, X, FileText, Mail, Phone, Info, RefreshCw, Clock,
  Trophy, Building2,
} from 'lucide-react';
import {
  searchClubsForClaim, requestClaim, getMyClaims, submitEvidence,
  CLAIM_STATUSES, EVIDENCE_TYPES,
} from '../../services/claimService';
import { useRBAC } from '../../contexts/RBACContext';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const CLAIM_LABELS = {
  pt: {
    pageTitle: 'Vincular Dados Oficiais',
    pageSubtitle: 'Conecte sua instituição à base oficial de resultados',
    tabMyClaims: 'Meus Vínculos', tabSearch: 'Buscar Clube',
    sectionMyClaims: 'Seus Vínculos',
    sectionMyClaimsDesc: 'Status das suas solicitações de vínculo com dados oficiais',
    sectionSearch: 'Buscar Clube Oficial',
    sectionSearchDesc: 'Encontre o clube nos registros oficiais (CBDA, Federações)',
    infoBox: 'Busque pelo nome do clube como aparece nos resultados oficiais de natação. Após encontrar, envie uma solicitação de vínculo — nossa equipe irá verificar e aprovar em até 48 horas.',
    emptyTitle: 'Nenhum vínculo', emptyDesc: 'Busque o clube oficial e solicite o vínculo para acessar dados históricos.',
    emptyBtn: 'Buscar Clube', refresh: 'Atualizar',
    requestedAt: (d) => `Solicitado em ${d}`,
    alreadyRequested: 'Já solicitado',
    noResultsFor: (q) => `Nenhum clube encontrado para “${q}”`,
    searchPlaceholder: 'Buscar clube por nome... (ex: Minas, SESI, Flamengo)',
    // Status messages
    moreInfoNeeded: 'Informação adicional solicitada',
    sendEvidence: 'Enviar evidência',
    approvedMsg: 'Vínculo aprovado — dados oficiais disponíveis na plataforma.',
    rejectedMsg: 'Solicitação rejeitada', rejectionReason: 'Motivo:',
    retryBtn: 'Solicitar novamente',
    awaitingAnalysis: 'Aguardando início da análise...',
    underReview: 'Em análise pela equipe ArenaStats...',
    // Request modal
    modalTitle: 'Solicitar Vínculo', evidence: 'Comprovante (opcional)',
    details: 'Detalhes', cancel: 'Cancelar', submit: 'Enviar Solicitação',
    evidencePlaceholders: {
      cnpj: 'Informe o CNPJ (xx.xxx.xxx/xxxx-xx)',
      institutional_email: 'Seu e-mail institucional (ex: gestor@clube.com.br)',
      default: 'Descreva o documento ou situação...',
    },
    protectedClub: 'Clube protegido — revisão manual obrigatória',
    protectedTooltip: 'Clube protegido — requer revisão manual',
    // Evidence modal
    evidenceModalTitle: 'Enviar Evidência', evidenceType: 'Tipo de Evidência',
    reviewerAsked: 'Revisor pediu:', evidencePlaceholder: 'Descreva a evidência...',
    // Toasts
    toastSent: 'Solicitação enviada! Aguarde a análise.',
    toastError: 'Erro ao enviar solicitação',
    toastEvidenceSent: 'Evidência enviada! Aguarde nova análise.',
    toastEvidenceError: 'Erro ao enviar evidência',
    toastNetworkError: 'Erro de rede',
    noClub: 'Nenhum clube vinculado. Complete o onboarding primeiro.',
  },
  en: {
    pageTitle: 'Link Official Data',
    pageSubtitle: 'Connect your institution to the official results database',
    tabMyClaims: 'My Links', tabSearch: 'Search Club',
    sectionMyClaims: 'Your Links',
    sectionMyClaimsDesc: 'Status of your link requests with official data',
    sectionSearch: 'Search Official Club',
    sectionSearchDesc: 'Find the club in the official records (CBDA, Federations)',
    infoBox: 'Search by the club name as it appears in official swimming results. Once found, submit a link request — our team will verify and approve within 48 hours.',
    emptyTitle: 'No links yet', emptyDesc: 'Search for the official club and request a link to access historical data.',
    emptyBtn: 'Search Club', refresh: 'Refresh',
    requestedAt: (d) => `Requested on ${d}`,
    alreadyRequested: 'Already requested',
    noResultsFor: (q) => `No club found for “${q}”`,
    searchPlaceholder: 'Search club by name... (e.g. Minas, SESI, Flamengo)',
    // Status messages
    moreInfoNeeded: 'Additional information requested',
    sendEvidence: 'Submit evidence',
    approvedMsg: 'Link approved — official data is now available on the platform.',
    rejectedMsg: 'Request rejected', rejectionReason: 'Reason:',
    retryBtn: 'Request again',
    awaitingAnalysis: 'Awaiting review start...',
    underReview: 'Under review by the ArenaStats team...',
    // Request modal
    modalTitle: 'Request Link', evidence: 'Supporting evidence (optional)',
    details: 'Details', cancel: 'Cancel', submit: 'Send Request',
    evidencePlaceholders: {
      cnpj: 'Enter CNPJ (xx.xxx.xxx/xxxx-xx)',
      institutional_email: 'Your institutional email (e.g. manager@club.com)',
      default: 'Describe the document or situation...',
    },
    protectedClub: 'Protected club — manual review required',
    protectedTooltip: 'Protected club — requires manual review',
    // Evidence modal
    evidenceModalTitle: 'Submit Evidence', evidenceType: 'Evidence Type',
    reviewerAsked: 'Reviewer asked:', evidencePlaceholder: 'Describe the evidence...',
    // Toasts
    toastSent: 'Request submitted! Awaiting review.',
    toastError: 'Error submitting request',
    toastEvidenceSent: 'Evidence submitted! Awaiting new review.',
    toastEvidenceError: 'Error submitting evidence',
    toastNetworkError: 'Network error',
    noClub: 'No club linked. Complete onboarding first.',
  },
};

/* ─── Shared UI ────────────────────────────────────────────────────────── */

const StatusBadge = ({ status }) => {
  const s = CLAIM_STATUSES[status] || CLAIM_STATUSES.not_requested;
  return (
    <span className="text-xs font-semibold px-3 py-1 rounded-full"
      style={{ color: s.color, backgroundColor: s.bg }}>
      {s.label}
    </span>
  );
};

const SectionTitle = ({ icon: Icon, title, subtitle }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-1">
      {Icon && <Icon size={20} style={{ color: VUKZ.primary }} />}
      <h2 className="text-lg font-bold" style={{ color: VUKZ.text }}>{title}</h2>
    </div>
    {subtitle && <p className="text-sm" style={{ color: VUKZ.textLight }}>{subtitle}</p>}
  </div>
);

const InfoBox = ({ children }) => (
  <div className="rounded-xl border p-4 mb-5 flex gap-3"
    style={{ borderColor: VUKZ.border, backgroundColor: '#f9fafb' }}>
    <Info size={18} className="mt-0.5 flex-shrink-0" style={{ color: VUKZ.textLight }} />
    <div className="text-sm" style={{ color: VUKZ.textLight }}>{children}</div>
  </div>
);

/* ─── Main Page ────────────────────────────────────────────────────────── */

const ClaimOfficialDataPage = () => {
  const { clubId: institutionId } = useRBAC();
  const { lang } = useGovernanceLang();
  const t = CLAIM_LABELS[lang] || CLAIM_LABELS.pt;
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';

  // Tab: 'search' | 'my-claims'
  const [tab, setTab] = useState('my-claims');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  // Request modal
  const [selectedClub, setSelectedClub] = useState(null);
  const [evidenceType, setEvidenceType] = useState('none');
  const [evidenceDetail, setEvidenceDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // My claims
  const [claims, setClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(true);

  // Evidence submission (for needs_more_info)
  const [evidenceClaim, setEvidenceClaim] = useState(null);
  const [newEvType, setNewEvType] = useState('document');
  const [newEvDetail, setNewEvDetail] = useState('');
  const [submittingEvidence, setSubmittingEvidence] = useState(false);

  // Error / success
  const [toast, setToast] = useState(null);

  /* ── Load my claims ─────────────────────────────────────────────────── */
  const loadClaims = useCallback(async () => {
    if (!institutionId) { setLoadingClaims(false); return; }
    setLoadingClaims(true);
    try {
      const resp = await getMyClaims(institutionId);
      const data = resp?.data;
      setClaims(data ? [data] : []);
      // If no claims exist, show search tab
      if (!data) setTab('search');
    } catch (err) {
      console.error('Error loading claims:', err);
    } finally {
      setLoadingClaims(false);
    }
  }, [institutionId]);

  useEffect(() => { loadClaims(); }, [loadClaims]);

  /* ── Search ─────────────────────────────────────────────────────────── */
  const handleSearch = useCallback(async (q) => {
    if (!q || q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const results = await searchClubsForClaim(q);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  const onSearchChange = (val) => {
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(val), 400);
  };

  /* ── Request claim ──────────────────────────────────────────────────── */
  const handleRequest = async () => {
    if (!selectedClub) return;
    setSubmitting(true);
    try {
      const resp = await requestClaim({
        institution_id: institutionId,
        results_club_id: selectedClub.club_id,
        evidence_type: evidenceType,
        evidence_notes: evidenceDetail || null,
      });
      if (resp.success) {
        setToast({ type: 'success', msg: t.toastSent });
        setSelectedClub(null);
        setEvidenceType('none');
        setEvidenceDetail('');
        setSearchQuery('');
        setSearchResults([]);
        setTab('my-claims');
        loadClaims();
      } else {
        setToast({ type: 'error', msg: resp.error || t.toastError });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || t.toastNetworkError;
      setToast({ type: 'error', msg });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Submit additional evidence ─────────────────────────────────────── */
  const handleSubmitEvidence = async () => {
    if (!evidenceClaim) return;
    setSubmittingEvidence(true);
    try {
      const resp = await submitEvidence(evidenceClaim.claim_id, {
        evidence_type: newEvType,
        evidence_detail: newEvDetail,
      });
      if (resp.success) {
        setToast({ type: 'success', msg: t.toastEvidenceSent });
        setEvidenceClaim(null);
        setNewEvType('document');
        setNewEvDetail('');
        loadClaims();
      }
    } catch (err) {
      setToast({ type: 'error', msg: t.toastEvidenceError });
    } finally {
      setSubmittingEvidence(false);
    }
  };

  /* ── Toast auto-dismiss ─────────────────────────────────────────────── */
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  /* ── Render ─────────────────────────────────────────────────────────── */
  /* ── No club linked — same pattern as FinancialPage/SupportPage ────── */
  if (!institutionId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: VUKZ.bg }}>
        <p className="text-gray-500">{t.noClub}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* ── Header ─── */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: VUKZ_GRADIENT }}>
              <Shield size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold" style={{ color: VUKZ.text }}>
                {t.pageTitle}
              </h1>
              <p className="text-sm" style={{ color: VUKZ.textLight }}>
                {t.pageSubtitle}
              </p>
            </div>
          </div>
        </div>

        {/* ── Toast ─── */}
        {toast && (
          <div className={`rounded-xl border p-4 mb-5 flex items-center gap-3 ${
            toast.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            {toast.type === 'success' ?
              <CheckCircle2 size={18} className="text-green-600" /> :
              <AlertCircle size={18} className="text-red-600" />
            }
            <span className={`text-sm font-medium ${
              toast.type === 'success' ? 'text-green-700' : 'text-red-700'
            }`}>{toast.msg}</span>
            <button onClick={() => setToast(null)} className="ml-auto">
              <X size={14} className="text-gray-400" />
            </button>
          </div>
        )}

        {/* ── Tabs ─── */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
          <button
            onClick={() => setTab('my-claims')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              tab === 'my-claims' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
            }`}
            style={{ color: tab === 'my-claims' ? VUKZ.primary : VUKZ.textLight }}>
            {t.tabMyClaims}
            {claims.length > 0 && (
              <span className="ml-2 text-xs bg-gray-200 rounded-full px-2 py-0.5">{claims.length}</span>
            )}
          </button>
          <button
            onClick={() => setTab('search')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${
              tab === 'search' ? 'bg-white shadow-sm' : 'hover:bg-gray-50'
            }`}
            style={{ color: tab === 'search' ? VUKZ.primary : VUKZ.textLight }}>
            {t.tabSearch}
          </button>
        </div>

        {/* ═══════════ TAB: MY CLAIMS ═══════════ */}
        {tab === 'my-claims' && (
          <div>
            <SectionTitle icon={Trophy} title={t.sectionMyClaims}
              subtitle={t.sectionMyClaimsDesc} />

            {loadingClaims ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin" style={{ color: VUKZ.textLight }} />
              </div>
            ) : claims.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border" style={{ borderColor: VUKZ.border }}>
                <Building2 size={48} className="mx-auto mb-4" style={{ color: VUKZ.border }} />
                <h3 className="text-lg font-bold mb-2" style={{ color: VUKZ.text }}>{t.emptyTitle}</h3>
                <p className="text-sm mb-6" style={{ color: VUKZ.textLight }}>
                  {t.emptyDesc}
                </p>
                <button onClick={() => setTab('search')}
                  className="px-6 py-2.5 rounded-xl text-white text-sm font-bold transition hover:opacity-90"
                  style={{ background: VUKZ_GRADIENT }}>
                  {t.emptyBtn}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {claims.map(claim => (
                  <div key={claim.claim_id}
                    className="bg-white rounded-xl border p-5" style={{ borderColor: VUKZ.border }}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-base font-bold" style={{ color: VUKZ.text }}>
                          {claim.results_club_name || `Clube #${claim.results_club_id}`}
                        </h3>
                        <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>
                          {t.requestedAt(new Date(claim.requested_at).toLocaleDateString(locale))}
                        </p>
                      </div>
                      <StatusBadge status={claim.claim_status} />
                    </div>

                    {/* Confidence score */}
                    {claim.confidence_score != null && (
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{
                              width: `${claim.confidence_score}%`,
                              backgroundColor: claim.confidence_score >= 70 ? VUKZ.success :
                                claim.confidence_score >= 40 ? VUKZ.warning : VUKZ.error,
                            }} />
                        </div>
                        <span className="text-xs font-medium" style={{ color: VUKZ.textLight }}>
                          {claim.confidence_score}%
                        </span>
                      </div>
                    )}

                    {/* Status-specific actions */}
                    {claim.claim_status === 'needs_more_info' && (
                      <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                        <p className="text-sm font-medium text-orange-700 mb-2">
                          <AlertCircle size={14} className="inline mr-1" />
                          {t.moreInfoNeeded}
                        </p>
                        {claim.reviewer_notes && (
                          <p className="text-xs text-orange-600 mb-3 italic">"{claim.reviewer_notes}"</p>
                        )}
                        <button onClick={() => setEvidenceClaim(claim)}
                          className="text-sm font-bold text-orange-700 underline hover:text-orange-800">
                          {t.sendEvidence}
                        </button>
                      </div>
                    )}

                    {claim.claim_status === 'approved' && (
                      <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200">
                        <p className="text-sm font-medium text-green-700">
                          <CheckCircle2 size={14} className="inline mr-1" />
                          {t.approvedMsg}
                        </p>
                      </div>
                    )}

                    {claim.claim_status === 'rejected' && (
                      <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200">
                        <p className="text-sm font-medium text-red-700">
                          <X size={14} className="inline mr-1" />
                          {t.rejectedMsg}
                        </p>
                        {claim.rejection_reason && (
                          <p className="text-xs text-red-600 mt-1">{t.rejectionReason} {claim.rejection_reason}</p>
                        )}
                        <button onClick={() => setTab('search')}
                          className="text-sm font-bold text-red-700 underline hover:text-red-800 mt-2">
                          {t.retryBtn}
                        </button>
                      </div>
                    )}

                    {(claim.claim_status === 'requested' || claim.claim_status === 'under_review') && (
                      <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                        <p className="text-sm font-medium text-blue-700">
                          <Clock size={14} className="inline mr-1" />
                          {claim.claim_status === 'requested'
                            ? t.awaitingAnalysis
                            : t.underReview}
                        </p>
                      </div>
                    )}
                  </div>
                ))}

                <button onClick={loadClaims}
                  className="w-full py-2 text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 rounded-lg transition"
                  style={{ color: VUKZ.textLight }}>
                  <RefreshCw size={14} /> {t.refresh}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ TAB: SEARCH ═══════════ */}
        {tab === 'search' && (
          <div>
            <SectionTitle icon={Search} title={t.sectionSearch}
              subtitle={t.sectionSearchDesc} />

            <InfoBox>{t.infoBox}</InfoBox>

            {/* Search input */}
            <div className="relative mb-4">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: VUKZ.textLight }} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="w-full pl-10 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
                style={{ borderColor: VUKZ.border }}
              />
              {searching && (
                <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin"
                  style={{ color: VUKZ.textLight }} />
              )}
            </div>

            {/* Search results */}
            {searchResults.length > 0 && (
              <div className="border rounded-xl overflow-hidden mb-4" style={{ borderColor: VUKZ.border }}>
                {searchResults.map((club, idx) => (
                  <button key={club.club_id}
                    onClick={() => setSelectedClub(club)}
                    disabled={club.has_active_claim}
                    className={`w-full text-left px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition ${
                      idx > 0 ? 'border-t' : ''
                    } ${club.has_active_claim ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{ borderColor: VUKZ.border }}>
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2" style={{ color: VUKZ.text }}>
                        {club.club_name}
                        {club.is_protected && (
                          <Shield size={12} className="text-amber-500" title="Clube protegido — requer revisão manual" />
                        )}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: VUKZ.textLight }}>
                        {[club.city, club.state, club.country].filter(Boolean).join(', ')}
                        {club.total_athletes != null && ` • ${club.total_athletes} atletas`}
                        {club.total_results != null && ` • ${club.total_results} resultados`}
                      </div>
                    </div>
                    {club.has_active_claim ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-yellow-50 text-yellow-600 font-medium">
                      {t.alreadyRequested}
                      </span>
                    ) : (
                      <ChevronRight size={16} style={{ color: VUKZ.textLight }} />
                    )}
                  </button>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <p className="text-sm text-center py-6" style={{ color: VUKZ.textLight }}>
              {t.noResultsFor(searchQuery)}
              </p>
            )}
          </div>
        )}

        {/* ═══════════ MODAL: REQUEST CLAIM ═══════════ */}
        {selectedClub && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-lg font-bold" style={{ color: VUKZ.text }}>
                    {t.modalTitle}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
                    {selectedClub.club_name}
                  </p>
                </div>
                <button onClick={() => setSelectedClub(null)}
                  className="p-1 rounded-lg hover:bg-gray-100 transition">
                  <X size={18} />
                </button>
              </div>

              {/* Club summary */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex flex-wrap gap-4 text-sm" style={{ color: VUKZ.textLight }}>
                  {selectedClub.total_athletes != null && <span>🏊 {selectedClub.total_athletes} atletas</span>}
                  {selectedClub.total_results != null && <span>📊 {selectedClub.total_results} resultados</span>}
                  {selectedClub.first_year && <span>📅 {selectedClub.first_year} — {selectedClub.last_year}</span>}
                </div>
                {selectedClub.is_protected && (
                  <p className="text-xs mt-3 text-amber-600 flex items-center gap-1">
                    <Shield size={12} /> {t.protectedClub}
                  </p>
                )}
              </div>

              {/* Evidence type */}
              <div className="mb-4">
                <label className="text-sm font-semibold mb-2 block" style={{ color: VUKZ.text }}>
                  {t.evidence}
                </label>
                <select value={evidenceType}
                  onChange={(e) => setEvidenceType(e.target.value)}
                  className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  style={{ borderColor: VUKZ.border }}>
                  {EVIDENCE_TYPES.map(et => (
                    <option key={et.value} value={et.value}>{et.label}</option>
                  ))}
                </select>
              </div>

              {/* Evidence detail */}
              {evidenceType !== 'none' && (
                <div className="mb-5">
                  <label className="text-sm font-semibold mb-2 block" style={{ color: VUKZ.text }}>
                    {t.details}
                  </label>
                  <textarea value={evidenceDetail}
                    onChange={(e) => setEvidenceDetail(e.target.value)}
                    placeholder={
                      evidenceType === 'cnpj' ? t.evidencePlaceholders.cnpj :
                      evidenceType === 'institutional_email' ? t.evidencePlaceholders.institutional_email :
                      t.evidencePlaceholders.default
                    }
                    className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                    style={{ borderColor: VUKZ.border }}
                    rows={3}
                  />
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setSelectedClub(null)}
                  className="flex-1 py-2.5 px-4 rounded-xl border text-sm font-semibold transition hover:bg-gray-50"
                  style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
                  {t.cancel}
                </button>
                <button onClick={handleRequest}
                  disabled={submitting}
                  className="flex-1 py-2.5 px-4 rounded-xl text-white text-sm font-bold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: VUKZ_GRADIENT }}>
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Shield size={16} />}
                  {t.submit}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════ MODAL: SUBMIT EVIDENCE ═══════════ */}
        {evidenceClaim && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-lg font-bold" style={{ color: VUKZ.text }}>
                    {t.evidenceModalTitle}
                  </h3>
                  <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
                    Claim #{evidenceClaim.claim_id}
                  </p>
                </div>
                <button onClick={() => setEvidenceClaim(null)}
                  className="p-1 rounded-lg hover:bg-gray-100 transition">
                  <X size={18} />
                </button>
              </div>

              {evidenceClaim.reviewer_notes && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                  <p className="text-sm text-orange-700">
                    <strong>{t.reviewerAsked}</strong> "{evidenceClaim.reviewer_notes}"
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="text-sm font-semibold mb-2 block" style={{ color: VUKZ.text }}>
                  {t.evidenceType}
                </label>
                <select value={newEvType}
                  onChange={(e) => setNewEvType(e.target.value)}
                  className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                  style={{ borderColor: VUKZ.border }}>
                  {EVIDENCE_TYPES.filter(e => e.value !== 'none').map(et => (
                    <option key={et.value} value={et.value}>{et.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="text-sm font-semibold mb-2 block" style={{ color: VUKZ.text }}>
                  {t.details}
                </label>
                <textarea value={newEvDetail}
                  onChange={(e) => setNewEvDetail(e.target.value)}
                  placeholder={t.evidencePlaceholder}
                  className="w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none"
                  style={{ borderColor: VUKZ.border }}
                  rows={4}
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setEvidenceClaim(null)}
                  className="flex-1 py-2.5 px-4 rounded-xl border text-sm font-semibold transition hover:bg-gray-50"
                  style={{ borderColor: VUKZ.border, color: VUKZ.text }}>
                  {t.cancel}
                </button>
                <button onClick={handleSubmitEvidence}
                  disabled={submittingEvidence || !newEvDetail.trim()}
                  className="flex-1 py-2.5 px-4 rounded-xl text-white text-sm font-bold transition hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: VUKZ_GRADIENT }}>
                  {submittingEvidence ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                  {lang === 'en' ? 'Submit' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ClaimOfficialDataPage;
